import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { buildListingTemplate } from "@/lib/ebay";
import { LISTING_CHANNELS } from "@/lib/listing";

const CHAR_LIMITS = { eBay: 80, Etsy: 140 };

export default function EbayTemplateDialog({ open, onOpenChange, watch, servicesCount = 0 }) {
  const [channel, setChannel] = useState(watch?.listing_channel || "eBay");
  const composed = useMemo(
    () => buildListingTemplate(watch, { servicesCount, channel }),
    [watch, servicesCount, channel]
  );
  const [title, setTitle] = useState(composed.title);
  const [body, setBody] = useState(composed.body);
  const [copiedField, setCopiedField] = useState(null);
  const [dirty, setDirty] = useState(false);

  // When the dialog opens (or channel changes from the dropdown), re-seed
  // the textarea unless the user has unsaved edits.
  useEffect(() => {
    if (open) {
      setChannel(watch?.listing_channel || "eBay");
      setDirty(false);
      setCopiedField(null);
    }
  }, [open, watch]);

  useEffect(() => {
    if (!dirty) {
      setTitle(composed.title);
      setBody(composed.body);
    }
  }, [composed, dirty]);

  const onTitleChange = (v) => { setTitle(v); setDirty(true); };
  const onBodyChange = (v) => { setBody(v); setDirty(true); };

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(kind);
      toast.success(`${kind === "title" ? "Title" : "Description"} copied`);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast.error("Clipboard unavailable — select and copy manually.");
    }
  };

  const reset = () => {
    setTitle(composed.title);
    setBody(composed.body);
    setDirty(false);
    toast.info("Reset to template");
  };

  const charLimit = CHAR_LIMITS[channel] ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm max-w-3xl"
        data-testid="ebay-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#0A0604]">Listing Template</DialogTitle>
          <DialogDescription className="text-[#3A2A10]">
            Pick the channel, review, and copy. Placeholders in <span className="font-mono-data">[brackets]</span> are fields not tracked in the database — fill them in before posting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">Channel</Label>
              <Select value={channel} onValueChange={(v) => { setChannel(v); setDirty(false); }}>
                <SelectTrigger
                  className="h-10 bg-[#FBF3D9] border-[#8B6A1F]/45 rounded-sm focus:ring-[#D4AF37]/40"
                  data-testid="ebay-channel-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
                  {LISTING_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-[#3A2A10]">
              {dirty && (
                <span className="text-amber-600">• Edits preserved. Reset to regenerate from the template.</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">
                Listing Title {charLimit ? `(${channel} ${charLimit}-char limit)` : ""}
              </Label>
              {charLimit && (
                <span
                  className={`text-[10px] font-mono-data ${title.length > charLimit ? "text-rose-500" : "text-[#3A2A10]"}`}
                  data-testid="ebay-title-count"
                >
                  {title.length}/{charLimit}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                className="bg-[#FBF3D9] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
                data-testid="ebay-title-input"
              />
              <Button
                type="button"
                onClick={() => copy(title, "title")}
                className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm shrink-0"
                data-testid="ebay-copy-title"
              >
                {copiedField === "title" ? <CheckCircle2 size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
                Copy
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">Listing Description</Label>
            <Textarea
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              rows={18}
              className="bg-[#FBF3D9] border-[#8B6A1F]/45 rounded-sm font-mono-data text-xs focus-visible:ring-[#D4AF37]/40"
              data-testid="ebay-body-input"
            />
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={reset}
            className="bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.08] rounded-sm"
            data-testid="ebay-reset"
          >
            <RotateCcw size={14} className="mr-1.5" /> Reset to template
          </Button>
          <Button
            onClick={() => copy(body, "body")}
            className="h-10 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
            data-testid="ebay-copy-body"
          >
            {copiedField === "body" ? <CheckCircle2 size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
            Copy description
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
